export type EnvName = 'production' | 'staging' | 'development'

export interface BibeInfo {
  bibeNumber: string
  isProduction: boolean
}

export interface BibeHostnameInfo {
  bibePrefix: string
  effectiveHostname: string
  bibeType?: 'staging-bibes' | 'prod-bibes'
  bibeNumber?: string
  envModifier: string
  productSubdomain?: string
}

const ENV_MODIFIERS = ['dev', 'staging', 'local'] as const
const BIBE_TYPES = ['staging-bibes', 'prod-bibes'] as const

// Every apex BIBEs are served on. `buffertools.com` carries internal tools
// (hq), `bufferapp.com` the legacy REST API — a hostname on either is as much
// a BIBE as one on `buffer.com`, and reading it as a plain production host is
// what breaks env resolution. Longest first so the alternation can't settle on
// a shorter apex nested inside a longer one.
const APEX_DOMAINS = ['buffertools.com', 'bufferapp.com', 'buffer.com'] as const

type EnvModifier = (typeof ENV_MODIFIERS)[number]
type BibeType = (typeof BIBE_TYPES)[number]

const APEX_PATTERN = APEX_DOMAINS.map((domain) =>
  domain.replace(/\./g, String.raw`\.`),
).join('|')

function isEnvModifier(value: string | undefined): value is EnvModifier {
  return ENV_MODIFIERS.some((m) => m === value)
}

function isBibeType(value: string | undefined): value is BibeType {
  return BIBE_TYPES.some((t) => t === value)
}

// Hostname shape:
//   [<bibeNumber>.<bibeType>.]<subdomain>[.<env>].<apex>
// All groups but the apex are optional — bare `buffer.com`, plain
// `publish.buffer.com`, full `1.staging-bibes.publish.dev.buffer.com`,
// `1234.prod-bibes.hq.buffertools.com`, etc. all match.
const BIBE_HOSTNAME_RE = new RegExp(
  String.raw`^(?:(?<bibeNumber>\d+)\.(?<bibeType>${BIBE_TYPES.join('|')})\.)?` +
    String.raw`(?:(?<subdomain>[a-z0-9-]+(?:\.[a-z0-9-]+)*?)\.)?` +
    String.raw`(?:(?<env>${ENV_MODIFIERS.join('|')})\.)?` +
    String.raw`(?<apex>${APEX_PATTERN})$`,
)

declare global {
  // Stamped by @buffer-mono/vite-config's `define` with the active Vite mode
  // (e.g. 'development', 'dev-with-prod'). Absent outside Vite-built
  // frontends (node services, jest).
  // eslint-disable-next-line no-var -- `var` is required for a `declare global` augmentation
  var __BUFFER_VITE_MODE__: string | undefined
  // Stamped alongside __BUFFER_VITE_MODE__ only for the `:withBibe`-modified
  // modes — the PR number of the BIBE the local frontend targets.
  // eslint-disable-next-line no-var -- `var` is required for a `declare global` augmentation
  var __BUFFER_BIBE_NUMBER__: string | undefined
}

// Vite modes where the frontend is built locally but talks to a remote
// backend (`rushx build:watch:withProd` / `:withStaging`, optionally stacked
// with `:withBibe <PR number>`), mapped to the env modifier of that backend
// and, for a BIBE, the bibe type prefix to splice in ahead of it (the number
// itself comes from __BUFFER_BIBE_NUMBER__).
const REMOTE_BACKEND_MODE_CONFIG: Record<
  string,
  { envModifier: string; bibeType?: BibeType }
> = {
  'dev-with-prod': { envModifier: '' },
  'dev-with-staging': { envModifier: 'dev.' },
  'dev-with-prod-bibe': { envModifier: '', bibeType: 'prod-bibes' },
  'dev-with-staging-bibe': { envModifier: 'dev.', bibeType: 'staging-bibes' },
}

// The leading `(^|\.)` is captured and preserved so the suffix only matches a
// whole label: a hostname such as `api.notbuffer.com` is left alone. The apex
// is captured too, so swapping the env modifier can't also move the host to a
// different apex.
const ENV_MODIFIER_SUFFIX_RE = new RegExp(
  String.raw`(^|\.)(?:(?:${ENV_MODIFIERS.join('|')})\.)?(${APEX_PATTERN})$`,
)

// Same whole-label guard, keeping whichever apex the hostname already carries.
const APEX_SUFFIX_RE = new RegExp(String.raw`(^|\.)(${APEX_PATTERN})$`)

function getDefaultHostname(): string {
  const windowHostname =
    typeof window !== 'undefined' ? window.location.hostname : ''

  const remoteBackendConfig =
    globalThis.__BUFFER_VITE_MODE__ === undefined
      ? undefined
      : REMOTE_BACKEND_MODE_CONFIG[globalThis.__BUFFER_VITE_MODE__]

  if (remoteBackendConfig === undefined) {
    return windowHostname
  }

  // The frontend runs on a local hostname but the backend is remote: resolve
  // everything as if we were on the backend's hostname. An explicitly passed
  // hostname bypasses this (the default only applies when the argument is
  // omitted).
  const { effectiveHostname } = parseBibeHostname(windowHostname)
  const rewrittenHostname = effectiveHostname.replace(
    ENV_MODIFIER_SUFFIX_RE,
    `$1${remoteBackendConfig.envModifier}$2`,
  )

  if (remoteBackendConfig.bibeType && globalThis.__BUFFER_BIBE_NUMBER__) {
    return `${globalThis.__BUFFER_BIBE_NUMBER__}.${remoteBackendConfig.bibeType}.${rewrittenHostname}`
  }

  return rewrittenHostname
}

export function parseBibeHostname(
  hostname: string = getDefaultHostname(),
): BibeHostnameInfo {
  const lower = hostname.toLowerCase()
  const match = lower.match(BIBE_HOSTNAME_RE)

  // Non-buffer.com hostnames (localhost, third-party callbacks, raw IPs)
  // pass through with no env interpretation.
  if (!match) {
    return {
      bibePrefix: '',
      effectiveHostname: lower,
      envModifier: '',
      productSubdomain: lower.includes('.') ? lower.split('.')[0] : undefined,
    }
  }

  const groups = match.groups
  const bibeNumber = groups?.bibeNumber
  const bibeType = isBibeType(groups?.bibeType) ? groups.bibeType : undefined
  const subdomain = groups?.subdomain
  const env = isEnvModifier(groups?.env) ? groups.env : undefined
  // A match always captures an apex; the fallback only narrows the type.
  const apex = groups?.apex ?? 'buffer.com'

  const bibePrefix = bibeNumber && bibeType ? `${bibeNumber}.${bibeType}.` : ''
  const envModifier = env ? `${env}.` : ''
  const productSubdomain = subdomain?.split('.')[0]
  const effectiveHostname = subdomain
    ? `${subdomain}.${envModifier}${apex}`
    : `${envModifier}${apex}`

  return {
    bibePrefix,
    bibeType,
    bibeNumber,
    envModifier,
    productSubdomain,
    effectiveHostname,
  }
}

export function getBibeInfo(
  hostname: string = getDefaultHostname(),
): BibeInfo | null {
  const parsed = parseBibeHostname(hostname)

  if (!parsed.bibeNumber || !parsed.bibeType) {
    return null
  }

  return {
    bibeNumber: parsed.bibeNumber,
    isProduction: parsed.bibeType === 'prod-bibes',
  }
}

export function getEnvFromHostname(
  hostname: string = getDefaultHostname(),
): EnvName {
  const parsed = parseBibeHostname(hostname)

  if (parsed.bibeType) {
    return parsed.bibeType === 'prod-bibes' ? 'production' : 'staging'
  }

  if (parsed.envModifier === 'dev.' || parsed.envModifier === 'staging.') {
    return 'staging'
  }

  if (
    parsed.envModifier === 'local.' ||
    hostname.toLowerCase().startsWith('localhost')
  ) {
    return 'development'
  }

  return 'production'
}

export function getHostname(
  productionHostname: string,
  hostname: string = getDefaultHostname(),
): string {
  const { bibePrefix, envModifier } = parseBibeHostname(hostname)

  const targetHostname = envModifier
    ? productionHostname.replace(APEX_SUFFIX_RE, `$1${envModifier}$2`)
    : productionHostname

  return `${bibePrefix}${targetHostname}`
}

// Mirrors the per-product `localPort` mapping in
// `domains/app-shell/src/common/constants/products.ts`. Subdomains that have no
// product entry (e.g. `login`, `graph`, `images`) intentionally stay unmapped
// so getUrl doesn't append a port for them.
const DEV_PORTS_BY_SUBDOMAIN: Record<string, string> = {
  publish: '8888',
  analyze: '8081',
  engage: '8080',
  'start-page': '9999',
  account: '8080',
}

export function getDevPort(subdomain: string | undefined): string {
  if (!subdomain) return ''

  return DEV_PORTS_BY_SUBDOMAIN[subdomain] ?? ''
}

/**
 * Convert a production URL into the env-appropriate URL for the current
 * hostname. Pass the canonical production URL (e.g. `https://graph.buffer.com`)
 * — env-stamped inputs (`https://graph.dev.buffer.com`) are not supported and
 * will double-stamp.
 *
 *   getUrl('https://graph.buffer.com')
 *     // on publish.buffer.com                      → https://graph.buffer.com/
 *     // on publish.dev.buffer.com                  → https://graph.dev.buffer.com/
 *     // on publish.local.buffer.com                → https://graph.local.buffer.com/
 *     // on 1.staging-bibes.publish.dev.buffer.com  → https://1.staging-bibes.graph.dev.buffer.com/
 *     // on 42.prod-bibes.publish.buffer.com        → https://42.prod-bibes.graph.buffer.com/
 *
 * The BIBE prefix carries across apexes, so a host on one apex resolves a URL
 * on another to the matching BIBE:
 *
 *   getUrl('https://hq.buffertools.com')
 *     // on 42.prod-bibes.publish.buffer.com  → https://42.prod-bibes.hq.buffertools.com/
 *
 * For mapped local subdomains (publish, account, analyze, engage, start-page),
 * appends the dev port when running on a `.local.buffer.com` host.
 */
export function getUrl(
  productionUrl: string,
  hostname: string = getDefaultHostname(),
): string {
  const normalizedUrl = /^https?:\/\//.test(productionUrl)
    ? productionUrl
    : `https://${productionUrl}`
  const parsed = new URL(normalizedUrl)
  parsed.hostname = getHostname(parsed.hostname, hostname)

  if (!parsed.port && getEnvFromHostname(hostname) === 'development') {
    const subdomain = parsed.hostname.split('.')[0]
    parsed.port = getDevPort(subdomain)
  }

  return parsed.toString()
}
